import React, { useState } from "react";
import AssignedUserSelect from "./AssignedUserSelect";
import AssociationSelect from "./AssociationSelect";
import { optionId, optionLabel, PRIMARY_REASON_LABELS } from "./common";
import "./Import.css";

// Render a contact/lead object generically: scalar entries only.
function scalarEntries(obj, limit = 8) {
	if (!obj || typeof obj !== "object") return [];
	const source =
		obj.fields && typeof obj.fields === "object" && !Array.isArray(obj.fields)
			? obj.fields
			: obj;
	return Object.entries(source)
		.filter(
			([k, v]) =>
				(typeof v === "string" || typeof v === "number") &&
				String(v).trim() !== "" &&
				!["primary", "is_primary", "primary_flag", "row", "row_index"].includes(
					k
				)
		)
		.slice(0, limit);
}

function isPrimaryContact(contact, primaryRowIndex) {
	return (
		contact?.primary === true ||
		contact?.is_primary === true ||
		contact?.primary_flag === true ||
		(primaryRowIndex !== null &&
			primaryRowIndex !== undefined &&
			contact?.row_index === primaryRowIndex)
	);
}

function IssueList({ issues }) {
	if (!issues || issues.length === 0) return null;
	return (
		<ul className="issueList">
			{issues.map((iss, i) => (
				<li
					key={i}
					className={`issueItem ${iss.blocking ? "blocking" : "warning"}`}>
					<span>{iss.blocking ? "⛔" : "⚠️"}</span>
					<span>{iss.message}</span>
				</li>
			))}
		</ul>
	);
}

export default function GroupPreviewStep({
	columns,
	groupBy,
	onAddGroupBy,
	onRemoveGroupBy,
	primaryFlagColumn,
	onSetPrimaryFlagColumn,
	strictGrouping,
	onSetStrictGrouping,
	defaults,
	onSetDefault,
	association,
	onSetAssociation,
	associationOptions,
	assignedUserId,
	onSetAssignedUserId,
	options,
	optionsStatus, // "idle" | "loading" | "ready" | "error"
	onRetryOptions,
	preview, // normalized preview or null
	previewLoading,
	onRunPreview,
	onBack,
	onNext,
}) {
	const [expanded, setExpanded] = useState(() => new Set());

	const toggleGroup = (key) => {
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	};

	const availableForGroupBy = columns.filter((c) => !groupBy.includes(c));

	const defaultsFields = [
		{ key: "campaign", label: "Campaign", list: options?.campaigns },
		{ key: "status", label: "Status", list: options?.statuses, required: true },
		{ key: "source", label: "Source", list: options?.sources },
		{ key: "group", label: "Group", list: options?.groups },
	];

	const canPreview = groupBy.length > 0 && !previewLoading;
	const canNext =
		!!preview && !preview.blocking && groupBy.length > 0 && !!defaults.status;

	let nextHint = "";
	if (!preview) nextHint = "Run the preview before executing.";
	else if (preview.blocking)
		nextHint = "Blocking validation issues — fix the file or mapping first.";
	else if (!defaults.status)
		nextHint = "Pick a default Status (required by Iris).";

	return (
		<div>
			<h2>👥 Group Rows & Preview</h2>
			<p style={{ marginBottom: 12 }}>
				Rows sharing the same group-by value collapse into one lead; every row
				becomes a contact record on that lead. Nothing is written during
				preview.
			</p>

			{/* Group-by picker */}
			<div className="importFormRow">
				<span className="importFormLabel">
					Group by column(s) <span className="requiredMark">*</span>
				</span>
				<select
					className="importSelect"
					value=""
					onChange={(e) => {
						if (e.target.value) onAddGroupBy(e.target.value);
					}}>
					<option value="">Add a column…</option>
					{availableForGroupBy.map((c) => (
						<option key={c} value={c}>
							{c}
						</option>
					))}
				</select>
				<div className="chipRow" style={{ margin: 0 }}>
					{groupBy.map((c, i) => (
						<span key={c} className="columnChip">
							<span className="chipOrder">{i + 1}.</span> {c}
							<button
								type="button"
								title="Remove"
								onClick={() => onRemoveGroupBy(c)}>
								✕
							</button>
						</span>
					))}
					{groupBy.length === 0 && (
						<span className="importMuted" style={{ fontSize: 12 }}>
							none selected yet
						</span>
					)}
				</div>
			</div>

			{/* Company matching (spec Change 1) */}
			<div className="importFormRow">
				<span className="importFormLabel">Company matching</span>
				<select
					className="importSelect"
					style={{ minWidth: 340 }}
					value={strictGrouping ? "strict" : "normalized"}
					onChange={(e) => onSetStrictGrouping(e.target.value === "strict")}>
					<option value="normalized">
						Normalized (recommended): ignores LLC/Inc, punctuation, case
					</option>
					<option value="strict">Strict: exact text match</option>
				</select>
			</div>

			{/* Primary flag column */}
			<div className="importFormRow">
				<span className="importFormLabel">Primary-contact flag column</span>
				<select
					className="importSelect"
					value={primaryFlagColumn}
					onChange={(e) => onSetPrimaryFlagColumn(e.target.value)}>
					<option value="">
						(none — ranked automatically: most complete contact, then title
						seniority, then file order)
					</option>
					{columns.map((c) => (
						<option key={c} value={c}>
							{c}
						</option>
					))}
				</select>
			</div>

			{/* Association (optional). With live dropdown options the value must be
			    one of them — Iris rejects anything else — so the picker is a
			    searchable list, not free text. Free text stays available only when
			    the option list came back empty. */}
			<div className="importFormRow">
				<span className="importFormLabel">Association (optional)</span>
				{associationOptions && associationOptions.length > 0 ? (
					<>
						<AssociationSelect
							options={associationOptions}
							value={association}
							onChange={onSetAssociation}
						/>
						<span className="importMuted" style={{ fontSize: 11 }}>
							Type to search {associationOptions.length} Iris options.
						</span>
					</>
				) : (
					<input
						className="importInput"
						placeholder="Association name…"
						value={association}
						onChange={(e) => onSetAssociation(e.target.value)}
					/>
				)}
			</div>

			{/* Batch defaults */}
			<h3>Batch defaults</h3>
			{optionsStatus === "loading" && (
				<p className="importMuted">🔄 Loading Iris options…</p>
			)}
			{optionsStatus === "error" && (
				<div className="resultBanner error">
					Failed to load Iris campaign/status/source/group options.{" "}
					<button
						type="button"
						className="importBtn secondary small"
						onClick={onRetryOptions}>
						Retry
					</button>
				</div>
			)}
			{optionsStatus === "ready" &&
				defaultsFields.map(({ key, label, list, required }) => (
					<div className="importFormRow" key={key}>
						<span className="importFormLabel">
							{label}{" "}
							{required ? <span className="requiredMark">*</span> : null}
						</span>
						<select
							className="importSelect"
							value={defaults[key]}
							onChange={(e) => onSetDefault(key, e.target.value)}>
							<option value="">
								{required ? "Select…" : "(none)"}
							</option>
							{(list || []).map((opt) => (
								<option key={String(optionId(opt))} value={String(optionId(opt))}>
									{optionLabel(opt)}
								</option>
							))}
						</select>
					</div>
				))}

			{/* Assigned user (optional). Default is None = no assignment at all,
			    which leaves Iris's own behavior untouched. 300+ users, so the
			    picker is a type-ahead. */}
			{optionsStatus === "ready" && (
				<div className="importFormRow">
					<span className="importFormLabel">Assigned user (optional)</span>
					<AssignedUserSelect
						users={options?.users || []}
						value={assignedUserId}
						onChange={onSetAssignedUserId}
					/>
					<span className="importMuted" style={{ fontSize: 11 }}>
						{(options?.users || []).length > 0
							? `Type to search ${(options.users || []).length} Iris users. ` +
							  "None = leads are imported unassigned."
							: "No Iris users available — leads are imported unassigned."}
					</span>
				</div>
			)}

			<div style={{ marginTop: 16 }}>
				<button
					type="button"
					className="importBtn"
					disabled={!canPreview}
					onClick={onRunPreview}>
					{previewLoading ? "Running preview…" : "Run Preview (dry run)"}
				</button>
				{groupBy.length === 0 && (
					<span className="wizardNavHint" style={{ marginLeft: 12 }}>
						Pick at least one group-by column first.
					</span>
				)}
			</div>

			{/* Preview results */}
			{preview && (
				<div style={{ marginTop: 18 }}>
					<div
						className={`resultBanner ${
							preview.blocking ? "error" : "success"
						}`}>
						Preview: <b>{preview.groups.length}</b> lead
						{preview.groups.length === 1 ? "" : "s"} from{" "}
						<b>{preview.totalContacts}</b> contact row
						{preview.totalContacts === 1 ? "" : "s"}.
						{preview.blocking
							? " Blocking issues found — execution is disabled."
							: " No blocking issues."}
					</div>

					<IssueList issues={preview.issues} />

					{preview.groups.length === 0 && (
						<p className="importMuted" style={{ fontStyle: "italic" }}>
							The preview produced no leads — check your group-by column.
						</p>
					)}

					{preview.groups.map((g) => {
						const open = expanded.has(g.key);
						const hasBlocking = g.issues.some((i) => i.blocking);
						const hasWarning = g.issues.length > 0 && !hasBlocking;
						return (
							<div className="previewGroup" key={g.key}>
								<div
									className="previewGroupHeader"
									onClick={() => toggleGroup(g.key)}>
									<span className="previewGroupTitle">
										{hasBlocking ? "⛔ " : hasWarning ? "⚠️ " : ""}
										🏢 {g.key}
										{g.mergedNames && g.mergedNames.length > 1 && (
											<span
												className="importMuted"
												style={{ fontSize: 11, fontWeight: 400, marginLeft: 6 }}
												title={`Merged spellings: ${g.mergedNames.join(", ")}`}>
												(merged {g.mergedNames.length} spellings)
											</span>
										)}
									</span>
									<span className="previewGroupMeta">
										<span>
											{g.contacts.length} contact
											{g.contacts.length === 1 ? "" : "s"}
										</span>
										<span>{open ? "▲" : "▼"}</span>
									</span>
								</div>
								{open && (
									<div className="previewGroupBody">
										{g.mergedNames && g.mergedNames.length > 1 && (
											<p
												className="importMuted"
												style={{ fontSize: 12, marginTop: 0 }}>
												🔗 Merged {g.mergedNames.length} spellings into this
												lead: {g.mergedNames.join(", ")}
											</p>
										)}
										<IssueList issues={g.issues} />
										{scalarEntries(g.leadFields, 12).length > 0 && (
											<div className="previewKV">
												{scalarEntries(g.leadFields, 12).map(([k, v]) => (
													<span className="kv" key={k}>
														<b>{k}:</b> {String(v)}
													</span>
												))}
											</div>
										)}
										{g.contacts.length === 0 ? (
											<p className="importMuted" style={{ fontSize: 12 }}>
												No contact rows in this group.
											</p>
										) : (
											g.contacts.map((contact, ci) => (
												<div className="previewContact" key={ci}>
													<span style={{ fontWeight: 600, fontSize: 12 }}>
														Contact {ci + 1}
														{isPrimaryContact(contact, g.primaryRowIndex) && (
															<>
																<span className="primaryBadge">PRIMARY</span>
																{g.primaryReason && (
																	<span
																		className="importMuted"
																		style={{
																			fontSize: 11,
																			fontWeight: 400,
																			marginLeft: 6,
																		}}>
																		(primary:{" "}
																		{PRIMARY_REASON_LABELS[g.primaryReason] ??
																			g.primaryReason}
																		)
																	</span>
																)}
															</>
														)}
													</span>
													<div className="previewKV" style={{ marginTop: 4 }}>
														{scalarEntries(contact).map(([k, v]) => (
															<span className="kv" key={k}>
																<b>{k}:</b> {String(v)}
															</span>
														))}
														{scalarEntries(contact).length === 0 && (
															<span className="importMuted">
																(no scalar fields to show)
															</span>
														)}
													</div>
												</div>
											))
										)}
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}

			<div className="wizardNav">
				<button type="button" className="importBtn secondary" onClick={onBack}>
					← Back
				</button>
				{!canNext && <span className="wizardNavHint">{nextHint}</span>}
				<button
					type="button"
					className="importBtn"
					disabled={!canNext}
					onClick={onNext}>
					Continue to Execute →
				</button>
			</div>
		</div>
	);
}
